import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { CookieService } from 'ngx-cookie-service';
import { of, Observable } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';

import { environment } from 'src/environments/environment';
import { AuthEndpoint } from '@app/constants/auth-constants';
import { JwtHelperService } from '@auth0/angular-jwt';
import { AppConfigService } from '@app/services/app-config.service';

import { Token } from '../auth.constants';
import { TokenRefreshResponse } from './token-refresh-response.model';
import { ITokenPayload } from './token-payload.model';

@Injectable({
  providedIn: 'root'
})
export class TokenService {
  constructor(
    private cookieService: CookieService,
    private http: HttpClient,
    private _jwtService: JwtHelperService,
    private _appConfigService: AppConfigService
  ) {}

  getAccessToken(): string {
    return this.cookieService.getAll()[Token.AccessToken];
  }

  getRefreshToken(): string {
    return this.cookieService.getAll()[Token.RefreshToken];
  }

  isRefreshTokenPresent(): boolean {
    return Boolean(this.getRefreshToken());
  }

  setAccessToken(accessToken: string): void {
    this.cookieService.set(Token.AccessToken, accessToken, undefined, '/', environment.domain, undefined, null);
  }

  setRefreshToken(refreshToken: string): void {
    this.cookieService.set(Token.RefreshToken, refreshToken, undefined, '/', environment.domain, undefined, null);
  }

  refreshTokens(): Observable<TokenRefreshResponse | undefined> {
    const customParams = { refreshToken: `${this.getRefreshToken()}` };
    const tokenRefreshUrl = this._appConfigService.config.authUrl.concat(AuthEndpoint.Token);

    return this.http.post(tokenRefreshUrl, customParams).pipe(
      tap((response: TokenRefreshResponse) => {
        this.setAccessToken(response.accessToken);
        this.setRefreshToken(response.refreshToken);
      }),
      catchError((err: HttpErrorResponse) => {
        return of(undefined);
      })
    );
  }

  getUserNameFromToken(): string {
    return this._decodeToken(this.getAccessToken())?.sub;
  }

  composeEmailFromToken(): string {
    return this.getUserNameFromToken()?.concat('@griddynamics.com');
  }

  deleteTokens(): void {
    this.cookieService.delete(Token.AccessToken, '/', environment.domain);
    this.cookieService.delete(Token.RefreshToken, '/', environment.domain);
  }

  private _getUserNameFromRefreshToken(): string {
    return this._decodeToken(this.getRefreshToken()).sub;
  }

  private _decodeToken(token: string): ITokenPayload {
    return this._jwtService.decodeToken(token);
  }
}
